<template>
  <v-card class="pa-4 card">
    <h3 class="text-center my-2">
      {{ isEditing ? $t('Atualizar saída') : $t('Nova saída') }}
    </h3>

    <v-dialog v-model="confirmDialog" max-width="400">
      <v-card class="pa-4 text-center card">
        <h3>{{ $t('Atenção') }}</h3>
        <br />
        <p>
          {{ $t('Ao confirmar, será efetuada a') }}
          <strong>{{ $t('saída de materiais do estoque') }}</strong
          >.
        </p>
        <span>{{ $t('Deseja prosseguir?') }}</span>

        <div class="d-flex justify-center mt-4">
          <v-btn
            class="secondary-button ma-0 mr-1"
            depressed
            @click="confirmDialog = false"
          >
            {{ $t('Cancelar') }}
          </v-btn>
          <v-btn class="primary-button ma-0" depressed @click="submit">
            {{ $t('Confirmar') }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-form ref="form">
      <v-row class="ma-0" dense>
        <v-col cols="12" sm="6">
          <span class="text ml-1">{{ $t('Quantidade') }}</span>
          <v-text-field
            v-if="itemUnitIsUnidade"
            :disabled="isEditing"
            v-model="exit.quantity"
            :rules="[
              (v) => (!!v && parseFloat(v) > 0) || 'Quantidade obrigatória',
            ]"
            hide-details="auto"
            type="number"
            class="primary-input"
            placeholder="0000"
            outlined
            @keypress="preventDot"
            @paste="roundOnPaste"
          ></v-text-field>
          <v-currency-field
            v-else
            :disabled="isEditing"
            v-model="exit.quantity"
            :rules="[(v) => !!v || 'Quantidade obrigatória']"
            :decimalLength="3"
            hide-details="auto"
            type="number"
            class="primary-input"
            placeholder="0000"
            outlined
          >
            <template #message="{ message }">
              {{ $t(message) }}
            </template>
          </v-currency-field>
        </v-col>

        <v-col>
          <span class="text ml-1">{{ $t('Data da saída') }}</span>

          <div class="d-flex">
            <date-picker
              ref="datepicker"
              v-model="exit.exitDate"
              style="width: 100%"
              mode="dateTime"
              is24hr
              :popover="{ visibility: 'click' }"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <input
                  :class="
                    requestDateTimeError ? 'date-input-error' : 'date-input'
                  "
                  :value="inputValue"
                  v-on="inputEvents"
                  v-mask="['##/##/#### ##:##']"
                />
                <span class="date-span-error" v-if="requestDateTimeError">
                  {{ $t('Data da entrada é requerida') }}
                </span>
              </template>
            </date-picker>
          </div>
        </v-col>

        <v-col cols="12">
          <span class="text ml-1">{{ $t('Motivo') }}</span>
          <v-textarea
            v-model="exit.reason"
            rows="6"
            :rules="[(v) => !!v || 'Motivo obrigatório']"
            hide-details="auto"
            class="primary-input"
            :placeholder="
              $t('Descreva em poucas palavras o motivo da saída de estoque')
            "
            outlined
          >
            <template #message="{ message }">
              {{ $t(message) }}
            </template>
          </v-textarea>
        </v-col>

        <v-col cols="12">
          <span class="text ml-1">{{ $t('Nota Fiscal') }}</span>
          <v-text-field
            v-model="exit.nf"
            hide-details="auto"
            class="primary-input"
            :placeholder="$t('Insira o número da NF')"
            outlined
          >
            <template #message="{ message }">
              {{ $t(message) }}
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </v-form>

    <div class="d-flex justify-end">
      <v-btn class="secondary-button" depressed @click="$emit('close')">
        {{ $t('Cancelar') }}
      </v-btn>
      <v-btn class="primary-button" depressed @click="validate">
        {{ isEditing ? $t('Atualizar') : $t('Cadastrar') }}
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import ApiService from '../../../../services/ApiService'
import FormatDate from '../../../../Utils/FormatDate'
import moment from 'moment'

export default {
  name: 'ManageStockExit',

  props: {
    exitProp: Object,
    stockItemId: Number,
    stockItemUnit: String,
    institutionId: Number,
  },

  data() {
    return {
      requestDateTimeError: false,
      confirmDialog: false,
      loading: false,
      exit: {
        exitDate: moment().format('YYYY-MM-DD HH:mm'),
      },
      apiService: new ApiService(),
      isEditing: false,
      user: {},
    }
  },

  mounted() {
    this.user = JSON.parse(localStorage.getItem('user'))

    this.exit.stockItemId = this.stockItemId
    this.exit.registrationUserId = this.user.id

    if (this.exitProp) {
      this.isEditing = true

      this.exit = { ...this.exitProp }
      this.exit.exitDate = this.exit.exitDate
        ? this.exit.exitDate.split('T')[0]
        : ''
    }
  },

  computed: {
    itemUnitIsUnidade() {
      if (!this.stockItemUnit) return true

      if (this.stockItemUnit == 'Unidade') return true

      return false
    },
  },

  methods: {
    preventDot(event) {
      if (event.key === '.' || event.key === ',') {
        event.preventDefault()
      }
    },
    roundOnPaste(event) {
      event.preventDefault()
      event.stopPropagation()
      const pasted = (event.clipboardData || window.clipboardData).getData(
        'text'
      )
      const integerPart = pasted.split(/[,.]/)[0].replace(/[^0-9]/g, '')
      if (integerPart) {
        const newValue = parseInt(integerPart)
        this.$set(this.exit, 'quantity', newValue)
      }
    },

    formatDate(date) {
      return FormatDate.dateTime(date)
    },

    validate() {
      if (this.$refs.form.validate()) {
        this.confirmDialog = true
      }
    },

    async submit() {
      if (this.$refs.form.validate()) {
        this.loading = true

        if (this.isEditing) {
          await this.apiService
            .put('stockExit/edit', this.exit)
            .then(() => {
              this.$toast.success(
                this.$t('Saída de estoque atualizada com sucesso')
              )
              this.loading = false
              this.confirmDialog = false
              this.$emit('confirm')
            })
            .catch(() => {
              this.loading = false
            })
        } else {
          await this.apiService
            .post('StockExit/add', this.exit)
            .then(() => {
              this.$toast.success(
                this.$t('Saída de estoque cadastrada com sucesso')
              )
              this.confirmDialog = false
              this.loading = false
              this.$emit('confirm')
            })
            .catch(() => {
              this.confirmDialog = false
              this.loading = false
            })
        }
      } else {
        this.requestDateTimeError = this.exit.exitDate == null
      }
    },
  },

  watch: {
    'exit.exitDate': {
      deep: true,
      handler() {
        this.requestDateTimeError = false
      },
    },
  },
}
</script>

<style scoped>
.card {
  border-radius: 10px !important;
}
.date-input {
  background: var(--primary-input-background) !important;
  font-family: var(--primary-input-font) !important;
  font-size: var(--primary-input-font-size) !important;
  font-weight: var(--primary-input-font-weigth) !important;
  line-height: var(--primary-input-line-height) !important;
  letter-spacing: var(--primary-input-letter-spacing) !important;
  border-radius: var(--primary-input-border-radius) !important;
  height: 45px;
  width: 100%;
  border: 1px solid #707070 !important;
  padding-left: 10px !important;
}

.date-input-error {
  background: var(--primary-input-background) !important;
  font-family: var(--primary-input-font) !important;
  font-size: var(--primary-input-font-size) !important;
  font-weight: var(--primary-input-font-weigth) !important;
  line-height: var(--primary-input-line-height) !important;
  letter-spacing: var(--primary-input-letter-spacing) !important;
  border-radius: var(--primary-input-border-radius) !important;
  height: 45px;
  width: 100%;
  border: 2px solid #ff5252 !important;
  padding-top: 0 !important;
}
</style>
